import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { EngagementProductListWithUser } from '@epicuro-next/data/engagement/engagement-models';
import { isPowerList } from '@epicuro-next/features/engagement/list-utils';
import { List, Media } from '@epicuro-next/models/engagement';
import {
  APP_PRIVATE_PATHS,
  PRODUCT_LIST_SUBROUTES,
} from '@epicuro-next/navigation/constants';
import {
  loadListAllProducts,
  loadNextListProducts,
  QUERY_UPDATE_STRATEGY,
  resetListProducts,
  selectListEntityFactory,
  updateEntityList,
} from '@epicuro-next/state/list-entities';
import { actions } from '@epicuro-next/state/news-feed-state';
import { go, selectUrl } from '@epicuro-next/state/router-store';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import {
  selectListEditClickedFromLookbook,
  updateLookBookItemMedia,
} from '@epicuro-next/state/shop-the-look-state';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { selectUserEmail } from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { captureMessage } from '@sentry/angular';
import { concat, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  take,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';

import * as listActions from '../../actions';
import { IUpdateListWithProducts } from '../../models';
import {
  selectIsCurrentUserAllowedToEdit,
  selectIsOwnerOfOpenedList,
  selectListOldBulkAddStyleIdInput,
} from '../../selectors';
import { patchAddModel } from '../../utils/patch-add-model';

/**
 * ProductList Update Effects
 */
@Injectable()
export class UpdateEffects {
  private accountId$ = this.store.pipe(
    select(selectAccountId),
    filter(isNonNullish),
  );

  private siteId$ = this.store.pipe(select(selectSiteId), filter(isNonNullish));

  public updateProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listActions.updateProductList),
      concatLatestFrom(({ list }) => [
        this.accountId$,
        this.siteId$,
        this.store.pipe(select(selectListEntityFactory(list.id))),
      ]),
      switchMap(
        ([
          { list, media, skipRedirectionToList },
          accountId,
          siteId,
          oldList,
        ]) => {
          return this.updateSequence(
            list,
            media as Media,
            accountId,
            siteId,
            oldList?.list,
          ).pipe(
            withLatestFrom(
              this.store.pipe(select(selectIsCurrentUserAllowedToEdit)),
              this.store.pipe(select(selectIsOwnerOfOpenedList)),
            ),
            concatMap(
              ([item, isCurrentUserAllowedToEdit, isOwnerOfOpenedList]) => {
                const updateSuccess = listActions.updateProductListSuccess({
                  list: item,
                  isEditedByOtherAdminWithPermission:
                    isCurrentUserAllowedToEdit && !isOwnerOfOpenedList,
                  skipRedirectionToList,
                });

                if (media)
                  return [
                    updateSuccess,
                    updateLookBookItemMedia({ listId: item.id, media }),
                  ];
                else return [updateSuccess];
              },
            ),
            catchError((error) =>
              of(listActions.updateProductListFailed({ error })),
            ),
          );
        },
      ),
    ),
  );

  public captureListEditor$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          listActions.updateProductListSuccess,
          listActions.updateCommercialListSuccess,
        ),
        withLatestFrom(
          this.store.pipe(select(selectUserEmail)),
          this.store.pipe(select(selectIsCurrentUserAllowedToEdit)),
          this.store.pipe(select(selectIsOwnerOfOpenedList)),
        ),
        filter(
          ([, , isCurrentUserAllowedToEdit, isOwnerOfOpenedList]) =>
            (isCurrentUserAllowedToEdit as boolean) &&
            !(isOwnerOfOpenedList as boolean),
        ),
        tap(([{ list }, userEmail]) =>
          captureMessage(
            `Admin ${userEmail} edited not owned List: ${list.id}`,
          ),
        ),
      ),
    { dispatch: false },
  );

  /**
   * Updates the feed article and the list entity redux
   */
  public editListPublishedToFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        listActions.updateProductListSuccess,
        listActions.updateCommercialListSuccess,
      ),
      switchMap(({ list }) => [
        actions.editListArticle({ list }),
        updateEntityList({ list }),
      ]),
    ),
  );

  public updateProductListWithProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listActions.updateProductListWithProducts),
      concatLatestFrom(() => [
        this.accountId$,
        this.siteId$,
        this.store.pipe(select(selectListOldBulkAddStyleIdInput)),
      ]),
      filter(
        ([{ list }]: [
          IUpdateListWithProducts,
          string,
          string,
          boolean | undefined,
        ]) => isNonNullish(list.id),
      ),
      switchMap(
        ([
          { list, media, products, stylesOnly },
          accountId,
          siteId,
          stylesOnlyFromRule,
        ]) => {
          // change products should be before update, as update returns new list saved to store,
          // correct order guarantee correct product references in updated list.
          // In other side parallel calls depends on race condition
          return this.productListApi
            .changeProducts({
              id: list.id,
              patchItems: patchAddModel(products),
              stylesOnly: stylesOnly ?? stylesOnlyFromRule,
            })
            .pipe(
              concatLatestFrom(() => [
                this.store.pipe(select(selectListEntityFactory(list.id))),
              ]),
              switchMap(([, oldList]) => {
                return this.updateSequence(
                  list,
                  media as Media,
                  accountId,
                  siteId,
                  oldList?.list,
                );
              }),
              withLatestFrom(
                this.store.pipe(select(selectIsCurrentUserAllowedToEdit)),
                this.store.pipe(select(selectIsOwnerOfOpenedList)),
              ),
              concatMap(
                ([item, isCurrentUserAllowedToEdit, isOwnerOfOpenedList]) => {
                  if (isPowerList(item)) {
                    return [
                      updateEntityList({ list: item }),
                      listActions.updateProductListSuccess({
                        list: item,
                        isEditedByOtherAdminWithPermission:
                          isCurrentUserAllowedToEdit && !isOwnerOfOpenedList,
                      }),
                      loadListAllProducts({ listId: item.id }),
                    ];
                  }

                  return [
                    listActions.resetOpenedListProducts(),
                    resetListProducts({ listId: list.id }),
                    // update entity should be before loadNextListProducts
                    updateEntityList({ list: item }),
                    loadNextListProducts({
                      listId: list.id,
                      strategy: QUERY_UPDATE_STRATEGY.OVERWRITE,
                    }),
                    listActions.updateProductListSuccess({
                      list: item,
                      isEditedByOtherAdminWithPermission:
                        isCurrentUserAllowedToEdit && !isOwnerOfOpenedList,
                    }),
                  ];
                },
              ),
              catchError((error) =>
                of(listActions.updateProductListFailed({ error })),
              ),
            );
        },
      ),
    ),
  );

  public notifyOnProductListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listActions.updateProductListSuccess),
      map(({ list }) =>
        showSnackBarMessage({
          message: $localize`:@@UpdatedProductListSnackMessage:${
            list.name ?? 'List'
          } updated.`,
          messageType: MessageType.SUCCESS,
        }),
      ),
    ),
  );

  public notifyOnCommercialListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listActions.updateCommercialListSuccess),
      map(({ list }) =>
        showSnackBarMessage({
          message: $localize`:@@UpdatedCommercialListSnackMessage:${
            list.name ?? 'Commercial List'
          } updated.`,
          messageType: MessageType.SUCCESS,
        }),
      ),
    ),
  );

  public redirectOnProductListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        listActions.updateProductListSuccess,
        listActions.updateCommercialListSuccess,
      ),
      concatLatestFrom(() => this.store.pipe(select(selectUrl))),
      filter(([{ skipRedirectionToList }, url]) => {
        // don't redirect when User is already on details page (save power list)
        return (
          !skipRedirectionToList &&
          !url.includes(PRODUCT_LIST_SUBROUTES.details)
        );
      }),
      switchMap(([action]) =>
        this.store.pipe(
          select(selectListEditClickedFromLookbook),
          take(1),
          map((listEditClickedFromLookbook) => ({
            action,
            listEditClickedFromLookbook,
          })),
        ),
      ),
      map(({ action, listEditClickedFromLookbook }) => {
        let commands = [APP_PRIVATE_PATHS.productList.path, action.list.id];
        if (listEditClickedFromLookbook) {
          commands = [
            APP_PRIVATE_PATHS.shopTheLook.path,
            listEditClickedFromLookbook.lookbookId,
            listEditClickedFromLookbook.listId,
          ];
        }

        return go({ commands });
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private productListApi: ProductListApiService,
    private store: Store,
  ) {}

  private updateSequence(
    list: Partial<List> & Pick<List, 'id' | 'name'>,
    media: Media | null,
    accountId: string,
    siteId: string,
    oldList?: List | null,
  ) {
    const returnActions = [];

    if (media === null && oldList?.mainMedia) {
      returnActions.push(
        this.productListApi.deleteMedia(
          list.id,
          oldList.mainMedia.id as number,
        ),
      );
    }

    returnActions.push(
      this.productListApi.update({
        list,
        accountId,
        siteId,
        media: media as Media,
      }),
    );

    return concat(...returnActions).pipe(
      toArray(),
      map(
        (
          response:
            | [object, EngagementProductListWithUser]
            | [EngagementProductListWithUser],
        ) => {
          if (response.length === 1) {
            return response[0];
          }

          return response[1];
        },
      ),
    );
  }
}
